import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { NEVER, Observable } from 'rxjs';
import { shareReplay, switchMap } from 'rxjs/operators';
import { IdGenerator } from '../id-generator/id-generator.service';
import { CommentCreationModel, CommentModel } from '../../models';
import { CommentAction } from '../../store/actions';
import { CommentSelectors, selectCommentingState } from '../../store/selectors';
import { CommentingState } from '../../store/state';
import { OperationTrackerService } from '../operation-tracker/operation-tracker.service';

@Injectable()
export class CommentService {
  private _allStream$: Observable<CommentModel[]>;

  constructor(
    private store: Store<CommentingState>,
    private operationTrackerService: OperationTrackerService,
    private idGenerator: IdGenerator,
  ) {}

  getById(commentId: string): Observable<CommentModel> {
    return this.store.select(CommentSelectors.createSelectorById(commentId));
  }

  getByThreadId(threadId: string): Observable<CommentModel[]> {
    return this.store.select(CommentSelectors.createSelectorByThreadId(threadId));
  }

  getAll(): Observable<CommentModel[]> {
    if (!this._allStream$) {
      this._allStream$ = this.store.select(selectCommentingState).pipe(
        switchMap((commentState) => {
          if (commentState?.areAllLoaded) {
            return this.store.select(CommentSelectors.selectAll);
          }

          this.store.dispatch(CommentAction.LoadAll());
          return NEVER;
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      );
    }

    return this._allStream$;
  }

  /**
   * Creates new new comment for a specified thread
   * @param comment comment creation object
   * @returns created comment or throws error if operation is not success
   */
  async createAsync(comment: CommentCreationModel): Promise<CommentModel> {
    const operationId = this.idGenerator.generateId();
    this.store.dispatch(CommentAction.OptimisticCreate({ comment, operationId }));
    return this.operationTrackerService.throwIfOperationIsNotSuccessAsync(operationId);
  }

  updateBodyAsync(commentId: string, newBody: string, mentionedEmails?: string[]): Promise<void> {
    return this.updateBodyWithExtraParams(commentId, newBody, mentionedEmails);
  }

  /**
   * @deprecated Don't use it. It will be removed once the API starts storing extra params in db
   * @param commentId
   * @param newBody
   * @param mentionedEmails
   * @param extraParams
   * @returns
   */
  async updateBodyWithExtraParams(
    commentId: string,
    newBody: string,
    mentionedEmails?: string[],
    extraParams?: { [key: string]: any },
  ): Promise<void> {
    const operationId = this.idGenerator.generateId();
    this.store.dispatch(
      CommentAction.UpdateBody({
        commentId,
        newBody,
        mentionedEmails,
        extraParams,
        operationId,
      }),
    );
    await this.operationTrackerService.throwIfOperationIsNotSuccessAsync(operationId);
  }

  /**
   * Deletes comment
   * @param commentId
   */
  async deleteAsync(commentId: string): Promise<void> {
    const operationId = this.idGenerator.generateId();
    this.store.dispatch(CommentAction.Delete({ commentId, operationId }));
    await this.operationTrackerService.throwIfOperationIsNotSuccessAsync(operationId);
  }

  /**
   * Deletes batch comments
   * @param commentIds
   */
  async batchDeleteAsync(commentIds: string[]): Promise<void> {
    await Promise.all(commentIds.map((commentId) => this.deleteAsync(commentId)));
  }
}
